import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type {
  PersistedAttachment,
  PersistedMessage,
  PersistedToolEvent,
  UploadLimits,
} from "../../api";
import {
  authHeaders,
  compactConversation,
  fetchMessages,
  fetchUploadLimits,
  uploadAttachment,
} from "../../api";
import { config } from "../../config";
import { formatCost, turnCost } from "../../pricing";
import { ArtifactRenderer } from "../artifacts/ArtifactRenderer";
import type { Artifact } from "../artifacts/types";
import {
  AttachmentChip,
  type ComposerAttachment,
  extensionOf,
  formatBytes,
} from "./AttachmentChip";
import { ContextIndicator } from "./ContextIndicator";
import { MessageStats, type TurnUsage } from "./MessageStats";
import { StreamingIndicator } from "./StreamingIndicator";
import type { ThinkingEffort } from "./ThinkingEffortPicker";
import { ToolChip, type ToolChipData } from "./ToolChip";

type Role = "user" | "assistant";
type TextPart = { kind: "text"; id: string; text: string };
type ArtPart = { kind: "art"; id: string; artifact: Artifact };
type ToolPart = { kind: "tool"; id: string; data: ToolChipData };
type Part = TextPart | ArtPart | ToolPart;
type SentAttachment = { attachmentId: string; filename: string; byteSize: number };
type Message = {
  id: string;
  role: Role;
  parts: Part[];
  attachments?: SentAttachment[];
  usage?: TurnUsage;
  modelId?: string | null;
  effort?: string | null;
};

const messageText = (m: Message): string =>
  m.parts
    .filter((p): p is TextPart => p.kind === "text")
    .map((p) => p.text)
    .join("");

const appendText = (parts: Part[], chunk: string): Part[] => {
  const last = parts[parts.length - 1];
  if (last?.kind === "text") {
    return [...parts.slice(0, -1), { ...last, text: last.text + chunk }];
  }
  return [...parts, { kind: "text", id: localId("text"), text: chunk }];
};

const appendError = (prev: Message[], text: string): Message[] => {
  const last = prev[prev.length - 1];
  if (last?.role !== "assistant") return prev;
  return [...prev.slice(0, -1), { ...last, parts: appendText(last.parts, `\n[error] ${text}`) }];
};

const appendArtifact = (parts: Part[], artifact: Artifact): Part[] => [
  ...parts,
  { kind: "art", id: localId("art"), artifact },
];

const upsertToolEvent = (parts: Part[], data: ToolChipData): Part[] => {
  const idx = parts.findIndex((p) => p.kind === "tool" && p.data.toolCallId === data.toolCallId);
  if (idx === -1) {
    return [...parts, { kind: "tool", id: localId("tool"), data }];
  }
  const existing = parts[idx] as ToolPart;
  // Merge — completion event carries summary/error/duration; pending carries
  // the initial input_summary, which we don't want to drop if completion
  // omits it.
  const merged: ToolChipData = { ...existing.data, ...data };
  const next = [...parts];
  next[idx] = { ...existing, data: merged };
  return next;
};

type ToolEventPayload = {
  tool_call_id: string;
  tool?: string;
  status?: "pending" | "ok" | "error";
  input_summary?: string;
  summary?: string | null;
  error_message?: string | null;
  duration_ms?: number;
  result_payload?: Record<string, unknown> | null;
};

function toolEventToChip(p: ToolEventPayload): ToolChipData {
  return {
    toolCallId: p.tool_call_id,
    tool: p.tool ?? "",
    status: p.status ?? "pending",
    inputSummary: p.input_summary ?? "",
    summary: p.summary ?? null,
    errorMessage: p.error_message ?? null,
    durationMs: p.duration_ms,
    resultPayload: p.result_payload ?? null,
  };
}

function rebuildParts(
  content: string,
  artifacts: { spec: Artifact; text_offset: number }[] | undefined,
  toolEvents: PersistedToolEvent[] | undefined,
): Part[] {
  type Pin = { offset: number; kind: "art" | "tool"; payload: Part };
  const pins: Pin[] = [];
  for (const a of artifacts ?? []) {
    pins.push({
      offset: Math.min(Math.max(a.text_offset, 0), content.length),
      kind: "art",
      payload: { kind: "art", id: localId("art"), artifact: a.spec },
    });
  }
  for (const e of toolEvents ?? []) {
    pins.push({
      offset: Math.min(Math.max(e.text_offset, 0), content.length),
      kind: "tool",
      payload: {
        kind: "tool",
        id: localId("tool"),
        data: toolEventToChip({
          tool_call_id: e.tool_call_id,
          tool: e.tool,
          status: e.status,
          input_summary: e.input_summary,
          summary: e.summary,
          error_message: e.error_message,
          duration_ms: e.duration_ms,
          result_payload: e.result_payload,
        }),
      },
    });
  }
  if (!pins.length) return content ? [{ kind: "text", id: localId("text"), text: content }] : [];
  // Stable sort by offset; tool chip comes before its sibling artifact at the
  // same offset so users see "ran X" then the result it produced.
  pins.sort((a, b) => a.offset - b.offset || (a.kind === "tool" ? -1 : 1));
  const parts: Part[] = [];
  let cursor = 0;
  for (const pin of pins) {
    if (pin.offset > cursor)
      parts.push({ kind: "text", id: localId("text"), text: content.slice(cursor, pin.offset) });
    parts.push(pin.payload);
    cursor = Math.max(cursor, pin.offset);
  }
  if (cursor < content.length)
    parts.push({ kind: "text", id: localId("text"), text: content.slice(cursor) });
  return parts;
}

const persistedAttachments = (content: PersistedMessage["content"]): SentAttachment[] =>
  content
    .filter((b): b is { attachment: PersistedAttachment } => Boolean(b.attachment))
    .map((b) => ({
      attachmentId: b.attachment.attachment_id,
      filename: b.attachment.filename,
      byteSize: b.attachment.byte_size,
    }));

const largerCaps = (limits: UploadLimits): string => {
  const byCap = new Map<number, string[]>();
  for (const [ext, cap] of Object.entries(limits.max_bytes_by_extension ?? {})) {
    if (cap > limits.max_bytes) byCap.set(cap, [...(byCap.get(cap) ?? []), ext]);
  }
  return [...byCap].map(([cap, exts]) => ` (${formatBytes(cap)} for ${exts.join(", ")})`).join("");
};

let _localIdCounter = 0;
const localId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${_localIdCounter++}`;

const composerKey = (conversationId: string | null) =>
  `bedrock-chat:composer:${conversationId ?? "new"}`;

type Props = {
  model: string;
  effort: ThinkingEffort;
  conversationId: string | null;
  onConversationCreated: (id: string) => void;
  onConversationChanged: () => void;
};

export function ChatPane({
  model,
  effort,
  conversationId,
  onConversationCreated,
  onConversationChanged,
}: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState<string>(() => {
    try {
      return window.localStorage.getItem(composerKey(conversationId)) ?? "";
    } catch {
      return "";
    }
  });
  const [streaming, setStreaming] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [compacting, setCompacting] = useState(false);
  const [contextTokens, setContextTokens] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const justCreatedRef = useRef<Set<string>>(new Set());
  const userMessageRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const [pinUserVisible, setPinUserVisible] = useState(false);
  const [pending, setPending] = useState<ComposerAttachment[]>([]);
  const [limits, setLimits] = useState<UploadLimits | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepth = useRef(0);

  useEffect(() => {
    fetchUploadLimits()
      .then(setLimits)
      .catch(() => setLimits(null));
  }, []);

  const readyAttachments = useMemo(
    () => pending.filter((a) => a.state === "ready" && a.attachmentId),
    [pending],
  );
  const attachmentsBlocked = pending.some((a) => a.state !== "ready");

  const rejectFile = useCallback(
    (file: File, slotsLeft: number): string | null => {
      if (slotsLeft <= 0) {
        return `only ${limits?.max_documents_per_request ?? 5} files can ride along with a message`;
      }
      if (limits && !limits.extensions.includes(extensionOf(file.name))) {
        return `.${extensionOf(file.name) || "?"} is not a supported file type`;
      }
      const cap = limits
        ? (limits.max_bytes_by_extension?.[extensionOf(file.name)] ?? limits.max_bytes)
        : null;
      if (cap != null && file.size > cap) {
        return `${formatBytes(file.size)} exceeds the ${formatBytes(cap)} limit`;
      }
      if (file.size === 0) return "the file is empty";
      return null;
    },
    [limits],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (!files.length) return;
      const cap = limits?.max_documents_per_request ?? 5;
      const queued: ComposerAttachment[] = [];
      let slotsLeft = cap - pending.length;
      for (const file of files) {
        const rejection = rejectFile(file, slotsLeft);
        queued.push({
          localId: localId("att"),
          filename: file.name,
          byteSize: file.size,
          state: rejection ? "error" : "uploading",
          error: rejection ?? undefined,
        });
        if (!rejection) slotsLeft -= 1;
      }
      setPending((prev) => [...prev, ...queued]);
      files.forEach((file, i) => {
        const queuedItem = queued[i];
        if (queuedItem.state === "error") return;
        uploadAttachment(file)
          .then((ticket) =>
            setPending((prev) =>
              prev.map((a) =>
                a.localId === queuedItem.localId
                  ? { ...a, state: "ready", attachmentId: ticket.attachment_id }
                  : a,
              ),
            ),
          )
          .catch((err) =>
            setPending((prev) =>
              prev.map((a) =>
                a.localId === queuedItem.localId ? { ...a, state: "error", error: String(err) } : a,
              ),
            ),
          );
      });
    },
    [limits, pending.length, rejectFile],
  );

  const removeAttachment = useCallback((id: string) => {
    setPending((prev) => prev.filter((a) => a.localId !== id));
  }, []);

  const scrollToMessage = useCallback((id: string) => {
    const el = userMessageRefs.current.get(id);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const pinnedUser = useMemo(() => {
    const last = messages[messages.length - 1];
    if (last?.role !== "assistant") return null;
    const prev = messages[messages.length - 2];
    return prev?.role === "user" ? prev : null;
  }, [messages]);
  const pinnedUserText = pinnedUser ? messageText(pinnedUser) : "";
  const pinnedUserId = pinnedUser?.id ?? null;
  const showPin = Boolean(pinnedUser && !pinUserVisible);

  const conversationCost = useMemo(() => {
    let total = 0;
    let any = false;
    for (const m of messages) {
      if (m.role !== "assistant" || !m.usage) continue;
      const c = turnCost(m.modelId, m.usage.lastInputTokens, m.usage.totalOutputTokens);
      if (c != null) {
        total += c;
        any = true;
      }
    }
    return any ? total : null;
  }, [messages]);

  useEffect(() => {
    if (!pinnedUserId) {
      setPinUserVisible(false);
      return;
    }
    const userEl = userMessageRefs.current.get(pinnedUserId);
    const scrollEl = messagesScrollRef.current;
    if (!userEl || !scrollEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPinUserVisible(entry.isIntersecting),
      { root: scrollEl, threshold: 0 },
    );
    observer.observe(userEl);
    return () => observer.disconnect();
  }, [pinnedUserId]);

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      setPending([]);
      setContextTokens(0);
      setHistoryLoading(false);
      return;
    }
    // Conversations the SPA just created mid-stream are already in `messages`;
    // refetching here would race with the streaming reducer and wipe deltas
    // that arrive after the prop flips, so the first assistant turn renders
    // empty until reload.
    if (justCreatedRef.current.has(conversationId)) {
      justCreatedRef.current.delete(conversationId);
      return;
    }
    setPending([]);
    let cancelled = false;
    setHistoryLoading(true);
    fetchMessages(conversationId)
      .then((rows) => {
        if (cancelled) return;
        const rebuilt: Message[] = rows.map((r) => {
          const content = r.content
            .map((b) => (typeof b === "object" && "text" in b ? b.text : ""))
            .join("");
          return {
            id: localId(r.role[0]),
            role: r.role === "user" ? "user" : "assistant",
            parts: rebuildParts(content, r.artifacts, r.tool_events),
            attachments: persistedAttachments(r.content),
            usage:
              r.role === "assistant" && r.tokens_in
                ? {
                    lastInputTokens: r.tokens_in,
                    totalOutputTokens: r.tokens_out,
                    totalBilledTokens: r.tokens_in + r.tokens_out,
                  }
                : undefined,
            modelId: r.role === "assistant" ? (r.model_id ?? null) : null,
            effort: r.role === "assistant" ? (r.effort ?? null) : null,
          };
        });
        setMessages(rebuilt);
        const lastAssistant = [...rebuilt].reverse().find((m) => m.role === "assistant" && m.usage);
        setContextTokens(lastAssistant?.usage?.lastInputTokens ?? 0);
      })
      .catch(() => {
        // Surface as an empty state — non-fatal.
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: messages is the trigger
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  useEffect(() => {
    try {
      setInput(window.localStorage.getItem(composerKey(conversationId)) ?? "");
    } catch {
      setInput("");
    }
  }, [conversationId]);

  useEffect(() => {
    try {
      const key = composerKey(conversationId);
      if (input) window.localStorage.setItem(key, input);
      else window.localStorage.removeItem(key);
    } catch {}
  }, [input, conversationId]);

  const send = useCallback(async () => {
    const trimmed = input.trim();
    const sending: SentAttachment[] = readyAttachments.map((a) => ({
      attachmentId: a.attachmentId as string,
      filename: a.filename,
      byteSize: a.byteSize,
    }));
    if ((!trimmed && !sending.length) || streaming || attachmentsBlocked) return;

    const nextHistory: Message[] = [
      ...messages,
      {
        id: localId("u"),
        role: "user",
        parts: trimmed ? [{ kind: "text", id: localId("text"), text: trimmed }] : [],
        attachments: sending,
      },
      {
        id: localId("a"),
        role: "assistant",
        parts: [],
        modelId: model,
        effort,
      },
    ];
    setMessages(nextHistory);
    setInput("");
    setPending([]);
    setStreaming(true);

    try {
      const resp = await fetch(`${config.backendUrl}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          conversation_id: conversationId,
          messages: nextHistory
            .map((m) => ({
              role: m.role,
              content: messageText(m),
              attachments: (m.attachments ?? []).map((a) => a.attachmentId),
            }))
            .filter((m) => m.content || m.attachments.length),
          model,
          effort,
        }),
      });

      const convId = resp.headers.get("X-Conversation-Id");
      if (convId && !conversationId) {
        justCreatedRef.current.add(convId);
        onConversationCreated(convId);
      }

      if (!resp.ok) throw new Error(`chat failed: ${resp.status} ${await resp.text()}`);
      if (!resp.body) throw new Error("no body");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // The backend sends turn usage only at the end of a turn, so a stream
      // that closes without it was cut off, e.g. by the Lambda timeout.
      let usageSeen = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          if (line.startsWith("8:")) usageSeen = true;
          handleStreamLine(line, setMessages, setContextTokens);
        }
      }
      if (!usageSeen) {
        setMessages((prev) => appendError(prev, "The reply was cut off before it finished."));
      }
      onConversationChanged();
    } catch (err) {
      setMessages((prev) => appendError(prev, String(err)));
    } finally {
      setStreaming(false);
    }
  }, [
    input,
    streaming,
    messages,
    model,
    effort,
    conversationId,
    readyAttachments,
    attachmentsBlocked,
    onConversationCreated,
    onConversationChanged,
  ]);

  const compact = useCallback(async () => {
    if (!conversationId || compacting) return;
    setCompacting(true);
    try {
      const result = await compactConversation(conversationId);
      // Reload from server to reflect the rewritten history.
      const rows = await fetchMessages(conversationId);
      setMessages(
        rows.map((r) => {
          const content = r.content
            .map((b) => (typeof b === "object" && "text" in b ? b.text : ""))
            .join("");
          return {
            id: localId(r.role[0]),
            role: r.role === "user" ? "user" : "assistant",
            parts: rebuildParts(content, r.artifacts, r.tool_events),
            attachments: persistedAttachments(r.content),
          };
        }),
      );
      setContextTokens(0);
      onConversationChanged();
      // Surface what happened so the user understands the message count drop.
      console.info(`compacted ${result.messages_replaced} messages`);
    } catch (err) {
      console.error("compaction failed", err);
    } finally {
      setCompacting(false);
    }
  }, [conversationId, compacting, onConversationChanged]);

  return (
    <div className="chat-pane">
      {showPin && pinnedUser && (
        <div className="prompt-pin-bar">
          <PromptPin text={pinnedUserText} onClick={() => scrollToMessage(pinnedUser.id)} />
        </div>
      )}
      <div className="messages" ref={messagesScrollRef}>
        {historyLoading ? (
          <div className="empty-state">
            <p className="message-thinking">
              Loading conversation
              <span className="thinking-dots" aria-hidden="true" />
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <h2>{config.emptyStateTitle}</h2>
            {config.emptyStateBody && <p>{config.emptyStateBody}</p>}
          </div>
        ) : (
          messages.map((m, i) => {
            const isLast = i === messages.length - 1;
            const showStreaming = m.role === "assistant" && streaming && isLast;
            if (m.role === "user") {
              return (
                <div
                  key={m.id}
                  ref={(el) => {
                    userMessageRefs.current.set(m.id, el);
                  }}
                  className="message user"
                >
                  <div className="message-role">{m.role}</div>
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="message-attachments">
                      {m.attachments.map((a) => (
                        <AttachmentChip
                          key={a.attachmentId}
                          filename={a.filename}
                          byteSize={a.byteSize}
                        />
                      ))}
                    </div>
                  )}
                  {messageText(m) && <div className="message-content">{messageText(m)}</div>}
                </div>
              );
            }
            return (
              <div key={m.id} className={`message ${m.role}`}>
                <div className="message-role">{m.role}</div>
                {m.parts.map((p) => {
                  if (p.kind === "art")
                    return <ArtifactRenderer key={p.id} artifact={p.artifact} />;
                  if (p.kind === "tool") return <ToolChip key={p.id} data={p.data} />;
                  return p.text ? (
                    <div key={p.id} className="message-content markdown">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.text}</ReactMarkdown>
                    </div>
                  ) : null;
                })}
                {showStreaming && <StreamingIndicator />}
                <MessageStats usage={m.usage} modelId={m.modelId} effort={m.effort} />
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drop target only — the
          keyboard path to the same action is the Attach button below. */}
      <div
        className={`composer no-print${dragging ? " dragging" : ""}`}
        onDragEnter={(e) => {
          e.preventDefault();
          dragDepth.current += 1;
          if (e.dataTransfer.types.includes("Files")) setDragging(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          e.preventDefault();
          dragDepth.current -= 1;
          if (dragDepth.current <= 0) setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          dragDepth.current = 0;
          setDragging(false);
          addFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <div className="composer-inner">
          {pending.length > 0 && (
            <div className="composer-attachments">
              {pending.map((a) => (
                <AttachmentChip
                  key={a.localId}
                  filename={a.filename}
                  byteSize={a.byteSize}
                  state={a.state}
                  error={a.error}
                  onRemove={() => removeAttachment(a.localId)}
                />
              ))}
            </div>
          )}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={config.composerPlaceholder}
            disabled={streaming}
          />
          <div className="composer-bar">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              data-testid="attachment-input"
              accept={limits ? limits.extensions.map((e) => `.${e}`).join(",") : undefined}
              onChange={(e) => {
                addFiles(Array.from(e.target.files ?? []));
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="composer-attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={streaming}
              title={
                limits
                  ? `Attach up to ${limits.max_documents_per_request} files (${limits.extensions.join(", ")}), max ${formatBytes(limits.max_bytes)} each${largerCaps(limits)}`
                  : "Attach files"
              }
            >
              Attach
            </button>
            <ContextIndicator
              contextTokens={contextTokens}
              modelId={model}
              onCompact={compact}
              compacting={compacting}
              hasConversation={Boolean(conversationId)}
            />
            {conversationCost != null && (
              <span
                className="conversation-cost"
                title="Estimated total cost of this conversation (on-demand us-east-1 list price)"
              >
                {formatCost(conversationCost)}
              </span>
            )}
            <button
              type="button"
              className="composer-send"
              onClick={send}
              disabled={
                streaming || attachmentsBlocked || (!input.trim() && !readyAttachments.length)
              }
            >
              {streaming ? "Streaming…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PromptPin({ text, onClick }: { text: string; onClick: () => void }) {
  const firstLine = (text.split("\n")[0] ?? "").trim();
  return (
    <button
      type="button"
      className="prompt-pin"
      onClick={onClick}
      title="Scroll to original message"
    >
      <span className="prompt-pin-text">{firstLine || "(empty)"}</span>
    </button>
  );
}

function handleStreamLine(
  line: string,
  setMessages: (fn: (prev: Message[]) => Message[]) => void,
  setContextTokens: (n: number) => void,
) {
  if (!line) return;
  const idx = line.indexOf(":");
  if (idx === -1) return;
  const partType = line.slice(0, idx);
  const rest = line.slice(idx + 1);
  let payload: unknown;
  try {
    payload = JSON.parse(rest);
  } catch {
    return;
  }

  if (partType === "0" && typeof payload === "string") {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "assistant") return prev;
      return [...prev.slice(0, -1), { ...last, parts: appendText(last.parts, payload) }];
    });
  } else if (partType === "2" && Array.isArray(payload)) {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "assistant") return prev;
      let parts = last.parts;
      for (const a of payload as Artifact[]) {
        parts = appendArtifact(parts, a);
      }
      return [...prev.slice(0, -1), { ...last, parts }];
    });
  } else if (partType === "4" && typeof payload === "object" && payload !== null) {
    const chip = toolEventToChip(payload as ToolEventPayload);
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "assistant") return prev;
      return [...prev.slice(0, -1), { ...last, parts: upsertToolEvent(last.parts, chip) }];
    });
  } else if (partType === "8" && typeof payload === "object" && payload !== null) {
    const u = payload as TurnUsage;
    setContextTokens(u.lastInputTokens);
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "assistant") return prev;
      return [...prev.slice(0, -1), { ...last, usage: u }];
    });
  } else if (partType === "3") {
    setMessages((prev) => appendError(prev, String(payload)));
  }
}
